// Wire codec for NiuMessage envelopes.
//
// Encoded explicitly by field per payload type because the wire format mixes
// snake_case (tool_name, is_error, since_seq, latest_seq, bundle_id, app_version,
// device_id) with intentional camelCase (waitingForTool, questionId, messageId,
// blockIndex, inputDelta, mimeType). NEVER use a blanket camelCase/snake_case
// transformation here.

import type {
  NiuMessage,
  NiuMessageType,
  NiuPayload,
  Attachment,
  AgentDescriptor,
  UserMessagePayload,
  AssistantMessagePayload,
  ToolCallPayload,
  ToolResultPayload,
  ThinkingPayload,
  StatusPayload,
  ErrorPayload,
  ReactionPayload,
  QuestionPayload,
  QuestionOption,
  AnswerPayload,
  PresencePayload,
  CatchupPayload,
  CatchupCompletePayload,
  AgentsAvailablePayload,
  DeviceRegisterPayload,
  DeviceInfoPayload,
  DeviceUnregisterPayload,
  DevicesListRequestPayload,
  DeviceDeleteRequestPayload,
  DeviceRenameRequestPayload,
  DevicesListPayload,
  DeviceInfo,
  AssistantMessageDeltaPayload,
  ThinkingDeltaPayload,
  ToolCallDeltaPayload,
  MessageBatchPayload,
  FetchEarlierMessagesPayload,
  EarlierCompletePayload,
  InlineSnippetPayload,
  InlineSnippetReloadPayload,
  InlineSnippetClosedPayload,
  RateLimitPayload,
  RateLimitWindow,
} from './types';

export class NiuDecodeError extends Error {
  constructor(public readonly reason: string, public readonly raw: unknown) {
    super(`NiuDecodeError: ${reason}`);
    this.name = 'NiuDecodeError';
  }
}

const KNOWN_TYPES = new Set<NiuMessageType>([
  'user_message',
  'assistant_message',
  'tool_call',
  'tool_result',
  'thinking',
  'status',
  'error',
  'reaction',
  'agents_available',
  'device_register',
  'device_info',
  'device_unregister',
  'devices_list_request',
  'device_delete_request',
  'device_rename_request',
  'devices_list',
  'question',
  'answer',
  'presence',
  'catchup',
  'catchup_complete',
  'assistant_message_delta',
  'thinking_delta',
  'tool_call_delta',
  'message_batch',
  'fetch_earlier_messages',
  'earlier_complete',
  'inline_snippet',
  'inline_snippet_reload',
  'inline_snippet_closed',
  'rate_limit',
]);

function asObject(value: unknown, label: string): Record<string, unknown> {
  if (value == null || typeof value !== 'object' || Array.isArray(value)) {
    throw new NiuDecodeError(`${label} is not an object`, value);
  }
  return value as Record<string, unknown>;
}

function asString(value: unknown, label: string): string {
  if (typeof value !== 'string') throw new NiuDecodeError(`${label} not string`, value);
  return value;
}

function asOptString(value: unknown, label: string): string | undefined {
  if (value == null) return undefined;
  if (typeof value !== 'string') throw new NiuDecodeError(`${label} not string`, value);
  return value;
}

function asNumber(value: unknown, label: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    throw new NiuDecodeError(`${label} not number`, value);
  }
  return value;
}

function asOptNumber(value: unknown, label: string): number | undefined {
  if (value == null) return undefined;
  return asNumber(value, label);
}

function asBool(value: unknown, label: string): boolean {
  if (typeof value !== 'boolean') throw new NiuDecodeError(`${label} not boolean`, value);
  return value;
}

function decodeAttachment(raw: unknown): Attachment {
  const obj = asObject(raw, 'attachment');
  return {
    id: asString(obj.id, 'attachment.id'),
    name: asString(obj.name, 'attachment.name'),
    mimeType: asString(obj.mimeType, 'attachment.mimeType'),
    size: asNumber(obj.size, 'attachment.size'),
    url: asOptString(obj.url, 'attachment.url') ?? null,
    // PDF enrichment — present on received (and echoed) frames. Dropping
    // these here is what previously left received PDFs without a preview.
    pageCount: asOptNumber(obj.pageCount, 'attachment.pageCount'),
    previewUrl: asOptString(obj.previewUrl, 'attachment.previewUrl') ?? null,
  };
}

function decodeAttachments(raw: unknown): Attachment[] | undefined {
  if (raw == null) return undefined;
  if (!Array.isArray(raw)) throw new NiuDecodeError('attachments not array', raw);
  return raw.map(decodeAttachment);
}

function decodeAgent(raw: unknown): AgentDescriptor | undefined {
  if (raw == null) return undefined;
  const obj = asObject(raw, 'agent');
  return {
    id: asString(obj.id, 'agent.id'),
    name: asString(obj.name, 'agent.name'),
  };
}

// ---------- Payload decoders ----------

function decodeUserMessage(raw: unknown): UserMessagePayload {
  const obj = asObject(raw, 'user_message');
  return {
    text: asString(obj.text, 'user_message.text'),
    attachments: decodeAttachments(obj.attachments),
    agent: asOptString(obj.agent, 'user_message.agent'),
  };
}

function decodeAssistantMessage(raw: unknown): AssistantMessagePayload {
  const obj = asObject(raw, 'assistant_message');
  return {
    text: asString(obj.text, 'assistant_message.text'),
    agent: decodeAgent(obj.agent),
    attachments: decodeAttachments(obj.attachments),
  };
}

function decodeToolCall(raw: unknown): ToolCallPayload {
  const obj = asObject(raw, 'tool_call');
  return {
    toolName: asString(obj.tool_name, 'tool_call.tool_name'),
    input: asString(obj.input, 'tool_call.input'),
    toolUseId: asString(obj.tool_use_id, 'tool_call.tool_use_id'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeToolResult(raw: unknown): ToolResultPayload {
  const obj = asObject(raw, 'tool_result');
  return {
    toolUseId: asString(obj.tool_use_id, 'tool_result.tool_use_id'),
    output: asString(obj.output, 'tool_result.output'),
    isError: asBool(obj.is_error, 'tool_result.is_error'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeThinking(raw: unknown): ThinkingPayload {
  const obj = asObject(raw, 'thinking');
  return {
    text: asString(obj.text, 'thinking.text'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeStatus(raw: unknown): StatusPayload {
  const obj = asObject(raw, 'status');
  return {
    state: asString(obj.state, 'status.state'),
    detail: asOptString(obj.detail, 'status.detail'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeError(raw: unknown): ErrorPayload {
  const obj = asObject(raw, 'error');
  const out: ErrorPayload = {};
  const message = asOptString(obj.message, 'error.message');
  if (message !== undefined) out.message = message;
  const code = asOptString(obj.code, 'error.code');
  if (code !== undefined) out.code = code;
  const firstKnownSeq = asOptNumber(obj.first_known_seq, 'error.first_known_seq');
  if (firstKnownSeq !== undefined) out.firstKnownSeq = firstKnownSeq;
  const requestedSince = asOptNumber(obj.requested_since, 'error.requested_since');
  if (requestedSince !== undefined) out.requestedSince = requestedSince;
  const requestedBefore = asOptNumber(obj.requested_before, 'error.requested_before');
  if (requestedBefore !== undefined) out.requestedBefore = requestedBefore;
  return out;
}

function decodeReaction(raw: unknown): ReactionPayload {
  const obj = asObject(raw, 'reaction');
  // emoji can be string, null, or absent
  let emoji: string | null | undefined;
  if (obj.emoji === null) emoji = null;
  else if (typeof obj.emoji === 'string') emoji = obj.emoji;
  else if (obj.emoji === undefined) emoji = undefined;
  else throw new NiuDecodeError('reaction.emoji not string|null', obj.emoji);
  let alsoMatch: string[] | undefined;
  if (Array.isArray(obj.alsoMatch)) {
    alsoMatch = obj.alsoMatch.map((v, i) => asString(v, `reaction.alsoMatch[${i}]`));
  } else if (obj.alsoMatch !== undefined) {
    throw new NiuDecodeError('reaction.alsoMatch not array', obj.alsoMatch);
  }
  return {
    messageId: asString(obj.messageId, 'reaction.messageId'),
    ...(alsoMatch ? { alsoMatch } : {}),
    emoji,
    agent: decodeAgent(obj.agent),
  };
}

function decodeQuestionOption(raw: unknown): QuestionOption {
  const obj = asObject(raw, 'question.option');
  return {
    label: asString(obj.label, 'question.option.label'),
    selectedLabel: asOptString(obj.selectedLabel, 'question.option.selectedLabel'),
    value: asOptString(obj.value, 'question.option.value'),
  };
}

function decodeQuestion(raw: unknown): QuestionPayload {
  const obj = asObject(raw, 'question');
  if (!Array.isArray(obj.options)) throw new NiuDecodeError('question.options not array', obj.options);
  return {
    questionId: asString(obj.questionId, 'question.questionId'),
    title: asString(obj.title, 'question.title'),
    question: asString(obj.question, 'question.question'),
    options: obj.options.map(decodeQuestionOption),
    agent: decodeAgent(obj.agent),
  };
}

function decodeAnswer(raw: unknown): AnswerPayload {
  const obj = asObject(raw, 'answer');
  return {
    questionId: asString(obj.questionId, 'answer.questionId'),
    selectedOption: asString(obj.selectedOption, 'answer.selectedOption'),
  };
}

function decodePresence(raw: unknown): PresencePayload {
  const obj = asObject(raw, 'presence');
  const state = asString(obj.state, 'presence.state');
  if (state !== 'active' && state !== 'inactive') {
    throw new NiuDecodeError('presence.state invalid', state);
  }
  return { state };
}

function decodeCatchup(raw: unknown): CatchupPayload {
  const obj = asObject(raw, 'catchup');
  return {
    sinceSeq: asNumber(obj.since_seq, 'catchup.since_seq'),
    limit: asOptNumber(obj.limit, 'catchup.limit'),
  };
}

function decodeCatchupComplete(raw: unknown): CatchupCompletePayload {
  const obj = asObject(raw, 'catchup_complete');
  return {
    latestSeq: asNumber(obj.latest_seq, 'catchup_complete.latest_seq'),
    more: asBool(obj.more, 'catchup_complete.more'),
  };
}

function decodeAgentsAvailable(raw: unknown): AgentsAvailablePayload {
  const obj = asObject(raw, 'agents_available');
  if (!Array.isArray(obj.agents)) throw new NiuDecodeError('agents_available.agents not array', obj.agents);
  const agents = obj.agents.map((entry) => {
    const a = decodeAgent(entry);
    if (!a) throw new NiuDecodeError('agents_available.agents[] null', entry);
    return a;
  });
  return { agents };
}

function decodeDeviceRegister(raw: unknown): DeviceRegisterPayload {
  const obj = asObject(raw, 'device_register');
  return {
    token: asString(obj.token, 'device_register.token'),
    platform: asString(obj.platform, 'device_register.platform') as DeviceRegisterPayload['platform'],
    environment: asString(obj.environment, 'device_register.environment') as DeviceRegisterPayload['environment'],
    bundleId: asString(obj.bundle_id, 'device_register.bundle_id'),
    appVersion: asOptString(obj.app_version, 'device_register.app_version'),
    locale: asOptString(obj.locale, 'device_register.locale'),
    deviceId: asOptString(obj.device_id, 'device_register.device_id'),
  };
}

function decodeDeviceUnregister(raw: unknown): DeviceUnregisterPayload {
  const obj = asObject(raw, 'device_unregister');
  return {
    token: asString(obj.token, 'device_unregister.token'),
    environment: asString(obj.environment, 'device_unregister.environment') as DeviceUnregisterPayload['environment'],
  };
}

function decodeDeviceInfo(raw: unknown): DeviceInfoPayload {
  const obj = asObject(raw, 'device_info');
  const out: DeviceInfoPayload = {
    platform: asString(obj.platform, 'device_info.platform') as DeviceInfoPayload['platform'],
  };
  const deviceId = asOptString(obj.device_id, 'device_info.device_id');
  if (deviceId !== undefined) out.deviceId = deviceId;
  const bundleId = asOptString(obj.bundle_id, 'device_info.bundle_id');
  if (bundleId !== undefined) out.bundleId = bundleId;
  const appVersion = asOptString(obj.app_version, 'device_info.app_version');
  if (appVersion !== undefined) out.appVersion = appVersion;
  const locale = asOptString(obj.locale, 'device_info.locale');
  if (locale !== undefined) out.locale = locale;
  const deviceName = asOptString(obj.device_name, 'device_info.device_name');
  if (deviceName !== undefined) out.deviceName = deviceName;
  return out;
}

function decodeDevicesListRequest(_raw: unknown): DevicesListRequestPayload {
  return {};
}

function decodeDeviceDeleteRequest(raw: unknown): DeviceDeleteRequestPayload {
  const obj = asObject(raw, 'device_delete_request');
  return { deviceId: asString(obj.device_id, 'device_delete_request.device_id') };
}

function decodeDeviceRenameRequest(raw: unknown): DeviceRenameRequestPayload {
  const obj = asObject(raw, 'device_rename_request');
  return {
    deviceId: asString(obj.device_id, 'device_rename_request.device_id'),
    deviceName: asString(obj.device_name, 'device_rename_request.device_name'),
  };
}

function asOptStringOrNull(value: unknown, label: string): string | null {
  if (value === null) return null;
  if (value === undefined) return null;
  if (typeof value !== 'string') throw new NiuDecodeError(`${label} not string|null`, value);
  return value;
}

function decodeDevice(raw: unknown): DeviceInfo {
  const obj = asObject(raw, 'devices_list.device');
  const platformRaw = asOptStringOrNull(obj.platform, 'device.platform');
  const platform =
    platformRaw === 'ios' || platformRaw === 'macos' || platformRaw === 'android' || platformRaw === 'web'
      ? platformRaw
      : null;
  return {
    deviceId: asString(obj.device_id, 'device.device_id'),
    deviceName: asOptStringOrNull(obj.device_name, 'device.device_name'),
    platform,
    bundleId: asOptStringOrNull(obj.bundle_id, 'device.bundle_id'),
    appVersion: asOptStringOrNull(obj.app_version, 'device.app_version'),
    locale: asOptStringOrNull(obj.locale, 'device.locale'),
    firstSeen: asNumber(obj.first_seen, 'device.first_seen'),
    lastSeen: asNumber(obj.last_seen, 'device.last_seen'),
    connected: asBool(obj.connected, 'device.connected'),
  };
}

function decodeDevicesList(raw: unknown): DevicesListPayload {
  const obj = asObject(raw, 'devices_list');
  if (!Array.isArray(obj.devices)) throw new NiuDecodeError('devices_list.devices not array', obj.devices);
  return { devices: obj.devices.map(decodeDevice) };
}

function decodeAssistantDelta(raw: unknown): AssistantMessageDeltaPayload {
  const obj = asObject(raw, 'assistant_message_delta');
  return {
    messageId: asString(obj.messageId, 'assistant_message_delta.messageId'),
    blockIndex: asNumber(obj.blockIndex, 'assistant_message_delta.blockIndex'),
    delta: asString(obj.delta, 'assistant_message_delta.delta'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeThinkingDelta(raw: unknown): ThinkingDeltaPayload {
  const obj = asObject(raw, 'thinking_delta');
  return {
    messageId: asString(obj.messageId, 'thinking_delta.messageId'),
    blockIndex: asNumber(obj.blockIndex, 'thinking_delta.blockIndex'),
    delta: asString(obj.delta, 'thinking_delta.delta'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeToolCallDelta(raw: unknown): ToolCallDeltaPayload {
  const obj = asObject(raw, 'tool_call_delta');
  return {
    messageId: asString(obj.messageId, 'tool_call_delta.messageId'),
    blockIndex: asNumber(obj.blockIndex, 'tool_call_delta.blockIndex'),
    toolUseId: asString(obj.tool_use_id, 'tool_call_delta.tool_use_id'),
    inputDelta: asString(obj.inputDelta, 'tool_call_delta.inputDelta'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeMessageBatch(raw: unknown): MessageBatchPayload {
  const obj = asObject(raw, 'message_batch');
  if (!Array.isArray(obj.envelopes)) {
    throw new NiuDecodeError('message_batch.envelopes not array', obj.envelopes);
  }
  const envelopes: NiuMessage[] = [];
  for (const inner of obj.envelopes) {
    const decoded = decodeMessageObject(inner);
    if (decoded) envelopes.push(decoded);
  }
  return { envelopes };
}

function decodeFetchEarlierMessages(raw: unknown): FetchEarlierMessagesPayload {
  const obj = asObject(raw, 'fetch_earlier_messages');
  return {
    beforeSeq: asNumber(obj.before_seq, 'fetch_earlier_messages.before_seq'),
    limit: asOptNumber(obj.limit, 'fetch_earlier_messages.limit'),
  };
}

function decodeEarlierComplete(raw: unknown): EarlierCompletePayload {
  const obj = asObject(raw, 'earlier_complete');
  return {
    earliestSeq: asNumber(obj.earliest_seq, 'earlier_complete.earliest_seq'),
    more: asBool(obj.more, 'earlier_complete.more'),
  };
}

function decodeInlineSnippet(raw: unknown): InlineSnippetPayload {
  const obj = asObject(raw, 'inline_snippet');
  return {
    url: asString(obj.url, 'inline_snippet.url'),
    title: asString(obj.title, 'inline_snippet.title'),
    token: asString(obj.token, 'inline_snippet.token'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeInlineSnippetReload(raw: unknown): InlineSnippetReloadPayload {
  const obj = asObject(raw, 'inline_snippet_reload');
  return {
    token: asString(obj.token, 'inline_snippet_reload.token'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeInlineSnippetClosed(raw: unknown): InlineSnippetClosedPayload {
  const obj = asObject(raw, 'inline_snippet_closed');
  return {
    token: asString(obj.token, 'inline_snippet_closed.token'),
    agent: decodeAgent(obj.agent),
  };
}

function decodeRateLimitWindow(raw: unknown, label: string): RateLimitWindow | null {
  if (raw === null || raw === undefined) return null;
  const obj = asObject(raw, label);
  return {
    usedPercent: asNumber(obj.used_percent, `${label}.used_percent`),
    windowMinutes: asNumber(obj.window_minutes, `${label}.window_minutes`),
    resetsAt: asNumber(obj.resets_at, `${label}.resets_at`),
  };
}

function decodeRateLimit(raw: unknown): RateLimitPayload {
  const obj = asObject(raw, 'rate_limit');
  return {
    primary: decodeRateLimitWindow(obj.primary, 'rate_limit.primary'),
    secondary: decodeRateLimitWindow(obj.secondary, 'rate_limit.secondary'),
    updatedAt: asNumber(obj.updated_at, 'rate_limit.updated_at'),
    agent: decodeAgent(obj.agent),
  };
}

function decodePayload(type: NiuMessageType, raw: unknown): NiuPayload {
  switch (type) {
    case 'user_message':
      return { kind: 'user_message', data: decodeUserMessage(raw) };
    case 'assistant_message':
      return { kind: 'assistant_message', data: decodeAssistantMessage(raw) };
    case 'tool_call':
      return { kind: 'tool_call', data: decodeToolCall(raw) };
    case 'tool_result':
      return { kind: 'tool_result', data: decodeToolResult(raw) };
    case 'thinking':
      return { kind: 'thinking', data: decodeThinking(raw) };
    case 'status':
      return { kind: 'status', data: decodeStatus(raw) };
    case 'error':
      return { kind: 'error', data: decodeError(raw) };
    case 'reaction':
      return { kind: 'reaction', data: decodeReaction(raw) };
    case 'question':
      return { kind: 'question', data: decodeQuestion(raw) };
    case 'answer':
      return { kind: 'answer', data: decodeAnswer(raw) };
    case 'presence':
      return { kind: 'presence', data: decodePresence(raw) };
    case 'catchup':
      return { kind: 'catchup', data: decodeCatchup(raw) };
    case 'catchup_complete':
      return { kind: 'catchup_complete', data: decodeCatchupComplete(raw) };
    case 'agents_available':
      return { kind: 'agents_available', data: decodeAgentsAvailable(raw) };
    case 'device_register':
      return { kind: 'device_register', data: decodeDeviceRegister(raw) };
    case 'device_info':
      return { kind: 'device_info', data: decodeDeviceInfo(raw) };
    case 'device_unregister':
      return { kind: 'device_unregister', data: decodeDeviceUnregister(raw) };
    case 'devices_list_request':
      return { kind: 'devices_list_request', data: decodeDevicesListRequest(raw) };
    case 'device_delete_request':
      return { kind: 'device_delete_request', data: decodeDeviceDeleteRequest(raw) };
    case 'device_rename_request':
      return { kind: 'device_rename_request', data: decodeDeviceRenameRequest(raw) };
    case 'devices_list':
      return { kind: 'devices_list', data: decodeDevicesList(raw) };
    case 'assistant_message_delta':
      return { kind: 'assistant_message_delta', data: decodeAssistantDelta(raw) };
    case 'thinking_delta':
      return { kind: 'thinking_delta', data: decodeThinkingDelta(raw) };
    case 'tool_call_delta':
      return { kind: 'tool_call_delta', data: decodeToolCallDelta(raw) };
    case 'message_batch':
      return { kind: 'message_batch', data: decodeMessageBatch(raw) };
    case 'fetch_earlier_messages':
      return { kind: 'fetch_earlier_messages', data: decodeFetchEarlierMessages(raw) };
    case 'earlier_complete':
      return { kind: 'earlier_complete', data: decodeEarlierComplete(raw) };
    case 'inline_snippet':
      return { kind: 'inline_snippet', data: decodeInlineSnippet(raw) };
    case 'inline_snippet_reload':
      return { kind: 'inline_snippet_reload', data: decodeInlineSnippetReload(raw) };
    case 'inline_snippet_closed':
      return { kind: 'inline_snippet_closed', data: decodeInlineSnippetClosed(raw) };
    case 'rate_limit':
      return { kind: 'rate_limit', data: decodeRateLimit(raw) };
  }
}

/** Decode an already-parsed envelope object. `null` ⇒ unknown frame type. */
function decodeMessageObject(parsed: unknown): NiuMessage | null {
  const obj = asObject(parsed, 'envelope');
  const id = asString(obj.id, 'envelope.id');
  const rawType = asString(obj.type, 'envelope.type');
  if (!KNOWN_TYPES.has(rawType as NiuMessageType)) {
    return null;
  }
  const type = rawType as NiuMessageType;
  const seq = asOptNumber(obj.seq, 'envelope.seq');
  const payload = decodePayload(type, obj.payload);
  const message: NiuMessage = { id, type, payload };
  if (seq !== undefined) message.seq = seq;
  return message;
}

/**
 * Decode a raw inbound JSON string into a NiuMessage.
 * Returns `null` for unknown frame types (protocol §2/§8 — silently ignore).
 * Throws NiuDecodeError when a known type fails to decode (schema drift —
 * caller should log loudly).
 */
export function decodeMessage(text: string): NiuMessage | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(text);
  } catch (err) {
    throw new NiuDecodeError(`invalid JSON: ${(err as Error).message}`, text);
  }
  return decodeMessageObject(parsed);
}

// ---------- Encoders ----------

function encodeAttachment(a: Attachment): Record<string, unknown> {
  const out: Record<string, unknown> = {
    id: a.id,
    name: a.name,
    mimeType: a.mimeType,
    size: a.size,
  };
  if (a.url != null) out.url = a.url;
  if (a.pageCount != null) out.pageCount = a.pageCount;
  if (a.previewUrl != null) out.previewUrl = a.previewUrl;
  return out;
}

function encodeAgent(a: AgentDescriptor | undefined): Record<string, unknown> | undefined {
  if (!a) return undefined;
  return { id: a.id, name: a.name };
}

function omitUndefined(obj: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(obj)) if (v !== undefined) out[k] = v;
  return out;
}

function encodePayload(p: NiuPayload): Record<string, unknown> {
  switch (p.kind) {
    case 'user_message': {
      const d = p.data;
      return omitUndefined({
        text: d.text,
        attachments: d.attachments?.map(encodeAttachment),
        agent: d.agent,
      });
    }
    case 'assistant_message': {
      const d = p.data;
      return omitUndefined({
        text: d.text,
        agent: encodeAgent(d.agent),
        attachments: d.attachments?.map(encodeAttachment),
      });
    }
    case 'tool_call': {
      const d = p.data;
      return omitUndefined({
        tool_name: d.toolName,
        input: d.input,
        tool_use_id: d.toolUseId,
        agent: encodeAgent(d.agent),
      });
    }
    case 'tool_result': {
      const d = p.data;
      return omitUndefined({
        tool_use_id: d.toolUseId,
        output: d.output,
        is_error: d.isError,
        agent: encodeAgent(d.agent),
      });
    }
    case 'thinking': {
      return omitUndefined({ text: p.data.text, agent: encodeAgent(p.data.agent) });
    }
    case 'status': {
      const d = p.data;
      return omitUndefined({
        state: d.state,
        detail: d.detail,
        agent: encodeAgent(d.agent),
      });
    }
    case 'error':
      return omitUndefined({ message: p.data.message, code: p.data.code });
    case 'reaction': {
      const d = p.data;
      const out: Record<string, unknown> = {
        messageId: d.messageId,
      };
      if (d.alsoMatch && d.alsoMatch.length > 0) out.alsoMatch = d.alsoMatch;
      if (d.emoji !== undefined) out.emoji = d.emoji; // null preserved
      if (d.agent) out.agent = encodeAgent(d.agent);
      return out;
    }
    case 'question': {
      const d = p.data;
      return omitUndefined({
        questionId: d.questionId,
        title: d.title,
        question: d.question,
        options: d.options.map((o) =>
          omitUndefined({ label: o.label, selectedLabel: o.selectedLabel, value: o.value }),
        ),
        agent: encodeAgent(d.agent),
      });
    }
    case 'answer':
      return { questionId: p.data.questionId, selectedOption: p.data.selectedOption };
    case 'presence':
      return { state: p.data.state };
    case 'catchup':
      return omitUndefined({ since_seq: p.data.sinceSeq, limit: p.data.limit });
    case 'catchup_complete':
      return { latest_seq: p.data.latestSeq, more: p.data.more };
    case 'agents_available':
      return { agents: p.data.agents.map((a) => ({ id: a.id, name: a.name })) };
    case 'device_register': {
      const d = p.data;
      return omitUndefined({
        token: d.token,
        platform: d.platform,
        environment: d.environment,
        bundle_id: d.bundleId,
        app_version: d.appVersion,
        locale: d.locale,
        device_id: d.deviceId,
      });
    }
    case 'device_info': {
      const d = p.data;
      return omitUndefined({
        device_id: d.deviceId,
        platform: d.platform,
        bundle_id: d.bundleId,
        app_version: d.appVersion,
        locale: d.locale,
        device_name: d.deviceName,
      });
    }
    case 'device_unregister':
      return { token: p.data.token, environment: p.data.environment };
    case 'devices_list_request':
      return {};
    case 'device_delete_request':
      return { device_id: p.data.deviceId };
    case 'device_rename_request':
      return { device_id: p.data.deviceId, device_name: p.data.deviceName };
    case 'devices_list':
      return {
        devices: p.data.devices.map((d) =>
          omitUndefined({
            device_id: d.deviceId,
            device_name: d.deviceName,
            platform: d.platform,
            bundle_id: d.bundleId,
            app_version: d.appVersion,
            locale: d.locale,
            first_seen: d.firstSeen,
            last_seen: d.lastSeen,
            connected: d.connected,
          }),
        ),
      };
    case 'assistant_message_delta':
    case 'thinking_delta': {
      const d = p.data;
      return omitUndefined({
        messageId: d.messageId,
        blockIndex: d.blockIndex,
        delta: d.delta,
        agent: encodeAgent(d.agent),
      });
    }
    case 'tool_call_delta': {
      const d = p.data;
      return omitUndefined({
        messageId: d.messageId,
        blockIndex: d.blockIndex,
        tool_use_id: d.toolUseId,
        inputDelta: d.inputDelta,
        agent: encodeAgent(d.agent),
      });
    }
    case 'message_batch':
      return {
        envelopes: p.data.envelopes.map((m) => JSON.parse(encodeMessage(m))),
      };
    case 'fetch_earlier_messages':
      return omitUndefined({ before_seq: p.data.beforeSeq, limit: p.data.limit });
    case 'earlier_complete':
      return { earliest_seq: p.data.earliestSeq, more: p.data.more };
    case 'inline_snippet':
      return omitUndefined({
        url: p.data.url,
        title: p.data.title,
        token: p.data.token,
        agent: encodeAgent(p.data.agent),
      });
    case 'inline_snippet_reload':
    case 'inline_snippet_closed':
      return omitUndefined({ token: p.data.token, agent: encodeAgent(p.data.agent) });
    case 'rate_limit': {
      // Inbound-only frame; encoded only for round-trip completeness.
      const win = (w: RateLimitWindow | null) =>
        w ? { used_percent: w.usedPercent, window_minutes: w.windowMinutes, resets_at: w.resetsAt } : null;
      return omitUndefined({
        primary: win(p.data.primary),
        secondary: win(p.data.secondary),
        updated_at: p.data.updatedAt,
        agent: encodeAgent(p.data.agent),
      });
    }
  }
}

export function encodeMessage(message: NiuMessage): string {
  const envelope: Record<string, unknown> = {
    id: message.id,
    type: message.type,
    payload: encodePayload(message.payload),
  };
  if (message.seq !== undefined) envelope.seq = message.seq;
  return JSON.stringify(envelope);
}
